/**
 * Melhora a legibilidade dos nomes na videochamada RealtimeKit (UI kit RTK)
 * sem redesenhar o layout.
 *
 * O UI Kit usa size=sm + transform:scale(0.6) e height fixo de 16px em tiles
 * pequenos — isso deixa a fonte praticamente ilegível. Aqui só ajustamos
 * name-tag / regras do tile que afetam o name-tag.
 *
 * Tags `dyte-*` permanecem no seletor por compatibilidade com shadow DOM legado
 * do kit; a sala em produção usa `rtk-*` (@cloudflare/realtimekit-react-ui).
 */

const STYLE_ATTR = "data-constela-name-font";

const NAME_TAG_CSS = `
:host {
  font-size: 15px !important;
  line-height: 1.25 !important;
}
:host([size='sm']) {
  font-size: 14px !important;
}
span.name {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}
`;

const PARTICIPANT_TILE_CSS = `
/* Evita cortar o texto no tile pequeno (height: 16px no kit). */
:host([size='sm'][variant='solid']) ::slotted(rtk-name-tag),
:host([size='sm'][variant='solid']) rtk-name-tag,
:host([size='sm'][variant='solid']) ::slotted(dyte-name-tag),
:host([size='sm'][variant='solid']) dyte-name-tag {
  height: auto !important;
  min-height: 20px;
  max-width: calc(100% - 12px);
}

/* Suaviza o scale agressivo do kit (0.8 / 0.6) só no name-tag. */
@container participanttile (max-width: 300px) {
  ::slotted(rtk-name-tag),
  rtk-name-tag,
  ::slotted(dyte-name-tag),
  dyte-name-tag {
    transform-origin: 0 100%;
    transform: scale(0.92) !important;
  }
}

@container participanttile (max-width: 150px) {
  ::slotted(rtk-name-tag),
  rtk-name-tag[variant='solid'],
  ::slotted(dyte-name-tag),
  dyte-name-tag[variant='solid'] {
    transform-origin: 0 100%;
    transform: scale(0.82) !important;
  }
}

@media only screen and (max-width: 480px) and (orientation: portrait) {
  :host([size='sm'][variant='solid']) ::slotted(rtk-name-tag),
  :host([size='sm'][variant='solid']) rtk-name-tag,
  :host([size='sm'][variant='solid']) ::slotted(dyte-name-tag),
  :host([size='sm'][variant='solid']) dyte-name-tag {
    transform-origin: 0 100%;
    transform: scale(0.88) !important;
  }
}

@media only screen and (max-height: 480px) and (orientation: landscape) {
  :host([size='sm'][variant='solid']) ::slotted(rtk-name-tag),
  :host([size='sm'][variant='solid']) rtk-name-tag,
  :host([size='sm'][variant='solid']) ::slotted(dyte-name-tag),
  :host([size='sm'][variant='solid']) dyte-name-tag {
    transform-origin: 0 100%;
    transform: scale(0.88) !important;
  }
}
`;

function injectStyle(shadowRoot: ShadowRoot, cssText: string): void {
  if (shadowRoot.querySelector(`style[${STYLE_ATTR}]`)) return;
  const style = document.createElement("style");
  style.setAttribute(STYLE_ATTR, "true");
  style.textContent = cssText;
  shadowRoot.appendChild(style);
}

/**
 * Aplica o ajuste de fonte/legibilidade e observa tiles novos (participantes entrando).
 * Retorna cleanup do observer.
 */
export function enableDyteNameTagReadability(rootEl: HTMLElement | null): () => void {
  if (!rootEl || typeof MutationObserver === "undefined") {
    return () => undefined;
  }

  const observedShadows = new WeakSet<ShadowRoot>();
  const observers: MutationObserver[] = [];

  const scheduleApply = (() => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        apply();
      });
    };
  })();

  const watchShadow = (shadow: ShadowRoot) => {
    if (observedShadows.has(shadow)) return;
    observedShadows.add(shadow);
    const observer = new MutationObserver(scheduleApply);
    observer.observe(shadow, { childList: true, subtree: true });
    observers.push(observer);
  };

  const enhanceElement = (el: Element): void => {
    const tag = el.tagName?.toLowerCase?.();
    const shadow = (el as HTMLElement).shadowRoot;
    if (!shadow) return;

    watchShadow(shadow);

    if (tag === "dyte-name-tag" || tag === "rtk-name-tag") {
      injectStyle(shadow, NAME_TAG_CSS);
      try {
        if ("maxLength" in el) {
          (el as HTMLElement & { maxLength?: number }).maxLength = 28;
        }
      } catch {
        /* ignore */
      }
      return;
    }

    if (tag === "dyte-participant-tile" || tag === "rtk-participant-tile") {
      injectStyle(shadow, PARTICIPANT_TILE_CSS);
    }
  };

  const walk = (node: ParentNode | ShadowRoot): void => {
    const elements =
      node instanceof Element
        ? [node, ...Array.from(node.querySelectorAll("*"))]
        : Array.from(node.querySelectorAll("*"));

    for (const el of elements) {
      enhanceElement(el);
      const shadow = (el as HTMLElement).shadowRoot;
      if (shadow) walk(shadow);
    }
  };

  const apply = () => walk(rootEl);

  apply();

  const rootObserver = new MutationObserver(scheduleApply);
  rootObserver.observe(rootEl, { childList: true, subtree: true });
  observers.push(rootObserver);

  // Shadow roots internos do UI kit montam um pouco depois do React ref.
  const timers = [100, 400, 1200, 3000].map((ms) => window.setTimeout(apply, ms));

  return () => {
    observers.forEach((observer) => observer.disconnect());
    timers.forEach((id) => window.clearTimeout(id));
  };
}
